import { useAccountStore } from '@/stores/account';
import { useCortonaViewerStore } from '@/stores/cortonaViewer';

export default {
  resourceCache: {},
  pendingResourceRequests: {},
  apiUrl: '/api/v1/',
  // apiUrl: process.env.NODE_ENV === 'development' ? 'https://dev1.techpubs.venit3d.pl/api/v1/' : 'https://itg.techpubs.venit3d.pl/api/v1/',
  options(data) {
    const post = data ? {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json'
      }
    } : {};
    const content = {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      ...post
    };

    const accountStore = useAccountStore();
    if (accountStore.logon) {
      const accessToken = localStorage.getItem('accessToken');
      if (!content.headers) {
        content.headers = {};
      }
      content.headers.Authorization = `Bearer ${accessToken}`;
    }

    return content;
  },
  async fetch(uri, data, options = {}) {
    return fetch(this.apiUrl + uri, { ...this.options(data), ...options });
  },
  async getMenu(id = 1, lang) {
    const response = await this.fetch(`menu/${id}?lang=${lang}`);
    return (await response.json()).menuItem;
  },
  async search(query, vin = false, id = 1, lang) {
    const response = await this.fetch(`menu/query?id=${id}&q=${query}&lang=${lang}&type=${vin ? 's' : 'r'}`);
    return (await response.json()).menuItem;
  },
  async getResource(id, vin, lang) {
    const cacheKey = `${id}-${vin || ''}-${lang}`;
    if (this.resourceCache[cacheKey]) {
      return this.resourceCache[cacheKey];
    }
    if (this.pendingResourceRequests[cacheKey]) {
      return this.pendingResourceRequests[cacheKey];
    }

    const response = this.fetch(`resources/${id}?lang=${lang}&sn=${vin || ''}`)
      .then(res => {
        if (res.status === 200) {
          return res.json().then(data => {
            this.resourceCache[cacheKey] = data;
            setTimeout(() => {
              delete this.resourceCache[cacheKey];
            }, 5000);
            delete this.pendingResourceRequests[cacheKey];
            return data;
          });
        } else {
          delete this.pendingResourceRequests[cacheKey];
          throw new Error('Failed to fetch resource');
        }
      }).catch(error => {
        delete this.pendingResourceRequests[cacheKey];
        throw error;
      });

    this.pendingResourceRequests[cacheKey] = response;
    return response;
  },
  async getSerialNumberInfo(serialNumber) {
    const response = await this.fetch(`serial/${encodeURIComponent(serialNumber)}`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch serial number info');
    }
    return response.json();
  },
  async register(email, password, language) {
    const body = {
      email,
      password,
      language,
      origin: window.location.origin
    };
    const response = await this.fetch('auth/register', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to register');
    return await response.json();
  },
  async login(email, password, rememberMe) {
    const body = {
      email,
      password,
      rememberMe
    };
    const response = await this.fetch('auth/login', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to login');
    return await response.json();
  },
  async refreshToken(token) {
    const body = {
      refreshToken: token,
      rememberMe: true
    };
    const response = await this.fetch('auth/refresh-token', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to refresh token');
    return await response.json();
  },
  async currentUser() {
    const response = await this.fetch('users/current');
    this.checkResponse(response, 'Failed to get user information');
    return await response.json();
  },
  async changePersonalData(firstName, lastName) {
    const body = {
      firstName,
      lastName
    };
    const response = await this.fetch('users/current/personal-data', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to change personal data');
    return await response.json();
  },
  async deleteAddressData(id) {
    const response = await this.fetch(`users/current/address-data/${id}`, null, { method: 'DELETE' });
    this.checkResponse(response, 'Failed to delete address data');
    return await response.json();
  },
  async changeAddressData(newAddress) {
    const response = await this.fetch('users/current/address-data', newAddress, { method: 'POST' });
    this.checkResponse(response, 'Failed to change address data');
    return await response.json();
  },
  async changeCompanyData(newCompany) {
    const response = await this.fetch('users/current/company-data', newCompany, { method: 'POST' });
    this.checkResponse(response, 'Failed to change company data');
    return await response.json();
  },
  async userOrders(page, pageSize) {
    const response = await this.fetch(`users/orders?page=${page}&pageSize=${pageSize}`);
    this.checkResponse(response, 'Failed to get user orders');
    return await response.json();
  },
  async activationCheck(token) {
    const response = await this.fetch(`auth/activation?t=${token}`);
    this.checkResponse(response, 'Failed to check activation token');
    return await response.json();
  },
  async activation(token) {
    const body = { tokenUuid: token };
    const response = await this.fetch('auth/activation', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to activate user');
    return await response.json();
  },
  async activationResend(token) {
    const body = { tokenUuid: token };
    const response = await this.fetch('auth/activation/resend', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to resend activation email');
  },
  async userCart() {
    const response = await this.fetch('users/cart');
    this.checkResponse(response, 'Failed to get user cart');
    return await response.json();
  },
  async addCartItems(items) {
    const body = {
      items
    };
    const response = await this.fetch('users/cart/items', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to update items in cart');
    return await response.json();
  },
  async updateCart(item) {
    const body = {
      items: [{
        id: item.id,
        partName: item.partName,
        partNumber: item.partNumber,
        quantity: item.quantity,
        resourceId: item.resourceId,
        manuallyAdded: item.manual || false,
        note: item.note
      }]
    };
    const response = await this.fetch('users/cart/items', body, { method: 'POST' });
    this.checkResponse(response, 'Failed to update items in cart');
    return await response.json();
  },
  async removeFromCart(index) {
    const body = { items: [index] };
    const response = await this.fetch('users/cart/items', body, { method: 'DELETE' });
    this.checkResponse(response, 'Failed to remove from cart');
    return await response.json();
  },
  async sendOrder(request) {
    const response = await this.fetch('users/orders', request, { method: 'POST' });
    this.checkResponse(response, 'Failed to send order');
    return await response.json();
  },
  checkResponse(response, message) {
    if (response.status >= 400) {
      const cortonaViewerStore = useCortonaViewerStore();
      cortonaViewerStore.showToast({ message, type: 'error' });
      throw new Error(message);
    }
  }
};
